import React from 'react';
import { Table, TableColumn, TableSort } from '../ui';
import { DashboardRFQ } from '../../hooks/dashboardMappers';
import { RfqSort, RfqSortKey } from '../../hooks/useRfqList';
import { RfqStateTag, RfqProgress, timeAgo, formatDate } from './rfqDisplay';

/**
 * Table view of the RFQ list — the shared v2 `Table` system, never
 * hand-rolled rows. Column sorting drives the SAME sort state the hook owns
 * (each sortable column maps to a real stored field), so switching back to
 * cards keeps the order.
 */
interface RfqListTableProps {
    rows: DashboardRFQ[];
    sort: RfqSort;
    onSort: (sort: RfqSort) => void;
    loading?: boolean;
    onOpen: (id: string) => void;
    empty: React.ReactNode;
}

/** Column key → hook sort key; only mapped columns are sortable. */
const SORT_KEY: Record<string, RfqSortKey> = {
    project: 'name',
    state: 'stage',
    parts: 'parts',
    created: 'created',
    updated: 'updated',
};

const columns: TableColumn<DashboardRFQ>[] = [
    {
        key: 'project',
        header: 'Project',
        sortable: true,
        render: (row) => (
            <span className="min-w-0">
                <span className="flex min-w-0 items-center gap-s2">
                    <span className={`overflow-hidden text-ellipsis whitespace-nowrap text-[13px] ${row.name ? 'font-semibold text-ink' : 'font-medium text-ink-muted'}`}>
                        {row.name ?? 'Untitled project'}
                    </span>
                    {row.pendingOnEndUser && (
                        <span
                            className="shrink-0 whitespace-nowrap rounded-[6px] border border-warn-border bg-warn-bg px-[6px] py-[1px] text-[10.5px] font-semibold text-warn-ink"
                            title={row.endUserLabel ? `Technical details pending on ${row.endUserLabel}` : 'Technical details pending on the end user'}
                        >
                            {row.iAmEndUser ? 'Yours to fill' : 'With end user'}
                        </span>
                    )}
                </span>
                <span className="font-mono text-[11px] text-ink-muted">
                    {row.id}{row.owner ? ` · ${row.owner}` : ''}
                </span>
            </span>
        ),
    },
    { key: 'state', header: 'Status', width: '120px', sortable: true, render: (row) => <RfqStateTag row={row} /> },
    { key: 'progress', header: 'Progress', width: 'minmax(90px,130px)', render: (row) => <RfqProgress row={row} /> },
    { key: 'parts', header: 'Parts', width: '50px', mono: true, sortable: true, render: (row) => <>{row.partsCount}</> },
    { key: 'replies', header: 'Replies', width: '60px', mono: true, render: (row) => <>{row.repliesCount}</> },
    {
        key: 'created', header: 'Created', width: '100px', sortable: true,
        render: (row) => <span className="whitespace-nowrap text-[12px] text-ink-muted">{formatDate(row.createdAtIso)}</span>,
    },
    {
        key: 'updated', header: 'Updated', width: '80px', sortable: true,
        render: (row) => <span className="text-[12px] text-ink-muted">{timeAgo(row.updatedAtIso)}</span>,
    },
];

const RfqListTable: React.FC<RfqListTableProps> = ({ rows, sort, onSort, loading = false, onOpen, empty }) => {
    // Present the hook's sort as the matching column's indicator (if any).
    const columnKey = Object.keys(SORT_KEY).find((key) => SORT_KEY[key] === sort.key);
    const tableSort: TableSort | undefined = columnKey
        ? { key: columnKey, direction: sort.direction }
        : undefined;

    return (
        <Table<DashboardRFQ>
            aria-label="RFQs"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            sort={tableSort}
            onSortChange={(next) => {
                const key = SORT_KEY[next.key];
                if (key) onSort({ key, direction: next.direction });
            }}
            onRowClick={(row) => onOpen(row.id)}
            loading={loading}
            stickyHeader
            maxHeight={560}
            empty={empty}
        />
    );
};

export default RfqListTable;
